/**
 * Error Boundary Component
 * Catches and displays React errors gracefully
 */

import { Component, ReactNode, ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ 
            background: '#fef6f6', 
            border: '1px solid #e01e5a', 
            borderRadius: '8px', 
            padding: '16px',
            marginBottom: '16px'
          }}>
            <h2 style={{ margin: '0 0 8px 0', color: '#e01e5a' }}>An error occurred</h2>
            <p style={{ margin: '0' }}>
              {this.state.error?.message || 'An unexpected error occurred. Please try again.'}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false });
                window.location.reload();
              }}
              style={{
                marginTop: '12px',
                padding: '8px 16px',
                background: '#e01e5a',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Reload Page
            </button>
            {import.meta.env.DEV && this.state.errorInfo && (
              <details style={{ marginTop: '1rem', whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                <summary>Error Details (Dev Only)</summary>
                {this.state.errorInfo.componentStack}
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
